import { Controller } from "@hotwired/stimulus";

const COLLAPSE_TO_REORDER_LABEL = "Collapse all owners to reorder";
const REMOVE_CONFIRMATION = "Remove this owner? This owner will be removed when the form is saved.";
const REORDER_LABEL = "Drag to reorder owners";

export default class extends Controller {
  static targets = ["dragHandle", "dragTooltip", "owners", "template", "toggleAllLabel"];

  connect () {
    this.prepareDropZones();
    this.updateControls();
  }

  add () {
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, new Date().getTime());
    this.ownersTarget.insertAdjacentHTML("beforeend", html);
    this.prepareDropZones();
    this.updateControls();
  }

  remove (event) {
    if (!window.confirm(REMOVE_CONFIRMATION)) return;

    const owner = event.currentTarget.closest("[data-owner-card]");
    const persisted = Boolean(owner.querySelector("[data-owner-id]").value);

    if (persisted) {
      owner.querySelector("[data-owner-destroy]").value = "1";
      owner.classList.add("!hidden");
    } else {
      owner.remove();
    }

    this.prepareDropZones();
    this.updateControls();
  }

  toggleAll () {
    const shouldExpand = this.activeDetails.every(details => !details.open);
    this.activeDetails.forEach(details => { details.open = shouldExpand; });
    this.updateControls();
  }

  detailsChanged () {
    this.updateControls();
  }

  updateSummary (event) {
    const summary = event.target.closest("[data-owner-card]").querySelector("[data-owner-summary-name]");
    summary.textContent = event.target.value.trim() || "New owner";
  }

  stopHeaderToggle (event) {
    event.stopPropagation();
  }

  dragStart (event) {
    if (!this.allCollapsed) {
      event.preventDefault();
      return;
    }

    const owner = event.currentTarget;
    const ownerRect = owner.getBoundingClientRect();

    this.draggedOwner = owner;
    this.draggedOwnerOffsetY = event.clientY - ownerRect.top;
    this.draggedOwnerHeight = ownerRect.height;
    this.draggedOwner.classList.add("owner-card--dragging");
    event.dataTransfer.clearData();
    event.dataTransfer.effectAllowed = "move";
    // Firefox refuses to start a drag unless dataTransfer carries something. Nothing reads it back:
    // the drop target is resolved from pointer geometry, and order is read from the DOM on submit.
    event.dataTransfer.setData("text/plain", "owner-card");
  }

  activateDropZone (event) {
    if (!this.draggedOwner) return;

    const dropZone = this.overlappingDropZone(event.clientY);
    this.clearActiveDropZone();
    if (!dropZone) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    dropZone.classList.add("owner-drop-zone--active");
  }

  dragLeaveOwners (event) {
    const ownersRect = this.ownersTarget.getBoundingClientRect();
    const pointerInsideOwners = event.clientX >= ownersRect.left &&
      event.clientX <= ownersRect.right &&
      event.clientY >= ownersRect.top &&
      event.clientY <= ownersRect.bottom;
    if (pointerInsideOwners) return;

    this.clearActiveDropZone();
  }

  dropOnZone (event) {
    if (!this.draggedOwner) return;

    const dropZone = this.activeDropZone;
    if (!dropZone) return;

    event.preventDefault();
    this.ownersTarget.insertBefore(this.draggedOwner, dropZone.nextElementSibling);
  }

  dragEnd () {
    this.draggedOwner?.classList.remove("owner-card--dragging");
    this.prepareDropZones();
    this.draggedOwner = null;
    this.draggedOwnerOffsetY = null;
    this.draggedOwnerHeight = null;
  }

  get owners () {
    return Array.from(this.ownersTarget.children).filter(element => element.matches("[data-owner-card]"));
  }

  get activeOwners () {
    return this.owners.filter(owner => !owner.classList.contains("!hidden"));
  }

  get activeDetails () {
    return this.activeOwners.map(owner => owner.querySelector("sl-details"));
  }

  get allCollapsed () {
    return this.activeDetails.every(details => !details.open);
  }

  showDropZones (event) {
    if (!this.allCollapsed) return;

    this.positionChangingDropZones(event.currentTarget)
      .forEach(dropZone => dropZone.classList.add("owner-drop-zone--visible"));
  }

  hideDropZones () {
    if (this.draggedOwner) return;

    this.dropZones.forEach(dropZone => dropZone.classList.remove("owner-drop-zone--visible"));
  }

  prepareDropZones () {
    this.removeDropZones();

    this.activeOwners.forEach(owner => {
      owner.before(this.buildDropZone());
    });
    this.activeOwners.at(-1)?.after(this.buildDropZone());
  }

  buildDropZone () {
    const dropZone = document.createElement("li");
    dropZone.classList.add("owner-drop-zone");
    dropZone.dataset.ownerDropZone = true;
    dropZone.setAttribute("aria-hidden", "true");
    return dropZone;
  }

  clearActiveDropZone () {
    this.activeDropZone?.classList.remove("owner-drop-zone--active");
  }

  overlappingDropZone (pointerY) {
    const draggedOwnerTop = pointerY - this.draggedOwnerOffsetY;
    const draggedOwnerBottom = draggedOwnerTop + this.draggedOwnerHeight;

    return this.positionChangingDropZones(this.draggedOwner)
      .map(dropZone => {
        const dropZoneRect = dropZone.getBoundingClientRect();
        const overlap = Math.min(draggedOwnerBottom, dropZoneRect.bottom) -
          Math.max(draggedOwnerTop, dropZoneRect.top);

        return { dropZone, overlap };
      })
      .filter(({ overlap }) => overlap > 0)
      .sort((first, second) => second.overlap - first.overlap)
      .at(0)?.dropZone;
  }

  positionChangingDropZones (owner) {
    const ownerIndex = this.activeOwners.indexOf(owner);
    if (ownerIndex === -1) return [];

    return Array.from(this.dropZones).filter((_dropZone, dropZoneIndex) => {
      return dropZoneIndex !== ownerIndex && dropZoneIndex !== ownerIndex + 1;
    });
  }

  get activeDropZone () {
    return this.ownersTarget.querySelector(".owner-drop-zone--active");
  }

  get dropZones () {
    return this.ownersTarget.querySelectorAll("[data-owner-drop-zone]");
  }

  removeDropZones () {
    this.dropZones.forEach(dropZone => dropZone.remove());
  }

  updateControls () {
    const allCollapsed = this.allCollapsed;

    this.toggleAllLabelTarget.textContent = allCollapsed ? "Expand all" : "Collapse all";

    const dragHandleLabel = allCollapsed ? REORDER_LABEL : COLLAPSE_TO_REORDER_LABEL;
    this.owners.forEach(owner => { owner.draggable = allCollapsed; });
    this.dragTooltipTargets.forEach(tooltip => { tooltip.disabled = allCollapsed; });
    this.dragHandleTargets.forEach(handle => {
      handle.disabled = !allCollapsed;
      handle.label = dragHandleLabel;
    });

    this.updateRemoveControls();
  }

  updateRemoveControls () {
    const ownersRemovable = this.activeOwners.length > 1;

    this.activeOwners.forEach(owner => {
      owner.querySelector("[data-owner-remove-button]").disabled = !ownersRemovable;
      owner.querySelector("[data-owner-remove-tooltip]").disabled = ownersRemovable;
    });
  }
}
